// apps/web/src/features/admin/form-config/shared/ExpressionEditor.tsx
//
// SP6 Task 2 — shared builder-first expression editor over Task 1's
// expression-builder helpers (@capture/rules-engine), with a raw-text
// fallback for anything outside the builder's representable subset.
//
// Guarantee: BUILDER edits made through this component only ever call
// `onChange` with a string that has already round-tripped through
// `builderToExpression` + `validateTransactionExpression` successfully.
// This is enforced here, at the UI boundary — `builderToExpression` itself
// does not escape/reject quotes in string literals, so any OTHER caller
// constructing a BuilderModel programmatically is not covered by this
// guarantee. Within this component the guard is belt-and-suspenders: the
// side inputs block `'` outright (see SideEditor), and `commitModel` still
// re-validates the serialized string before every `onChange` call, so a
// malformed expression never propagates from here regardless of how the
// model was assembled. RAW mode is intentionally more permissive: it fires
// `onChange` on every keystroke (the publish wall gates invalid raw text
// elsewhere), but still surfaces the validator's message live.

'use client';

import { useId, useMemo, useRef, useState, useEffect } from 'react';
import {
  builderToExpression,
  expressionToBuilder,
  validateTransactionExpression,
  TXN_BUILTIN_SCALARS,
  type BuilderModel,
  type BuilderClause,
  type BuilderSide,
  type BuilderCollection,
  type BuilderAggFn,
  type BuilderOp,
} from '@capture/rules-engine';
import { FormulaTestPanel } from './FormulaTestPanel';
import './ExpressionEditor.css';

// ─── Types ──────────────────────────────────────────────────────────────

export interface ExpressionPickerData {
  /** Field options per row collection — built-ins + any operator-promoted field.<name>s. */
  collections: Record<BuilderCollection, string[]>;
  /** 'transaction.ordinal', …, plus any operator-promoted 'transaction.<field>'. */
  transactionRefs: string[];
  /** Allocation target codes, offered as a datalist for filter-value inputs. */
  codeSuggestions?: string[];
}

export interface ExpressionEditorProps {
  /** The stored expression — source of truth. */
  value: string;
  onChange: (expr: string) => void;
  pickerData: ExpressionPickerData;
  label?: string;
}

/** Built-in collection fields + the transaction scalars — for consumers
 *  without a job draft to derive picker data from (e.g. the export-format
 *  columns editor). The collection lists are curated UI subsets of the
 *  runtime row shapes; the transaction refs derive from the engine's
 *  TXN_BUILTIN_SCALARS, the same list `buildTransactionEvalContext`
 *  populates, so a new scalar can't drift out of the pickers. */
export const DEFAULT_PICKER_DATA: ExpressionPickerData = {
  collections: {
    check: ['amountCents', 'routing', 'account', 'checkNumber', 'documentId'],
    coupon: ['appliedAmountCents', 'documentId'],
    allocation: ['targetId', 'label', 'code', 'amountCents', 'source'],
  },
  transactionRefs: TXN_BUILTIN_SCALARS.map((scalar) => `transaction.${scalar}`),
};

// ─── Constants ──────────────────────────────────────────────────────────

const AGG_FNS: BuilderAggFn[] = ['TXN_SUM', 'TXN_COUNT', 'TXN_MIN', 'TXN_MAX'];
const COLLECTIONS: BuilderCollection[] = ['check', 'coupon', 'allocation'];
const COMPARISON_OPS: BuilderOp[] = ['==', '!=', '>', '>=', '<', '<='];
const CUSTOM_REF = '__custom__';
const APOSTROPHE_HINT = "quotes aren't supported in values";
const TOO_COMPLEX_TITLE = 'expression too complex for the builder — editing as text';

// ─── Model helpers ──────────────────────────────────────────────────────

function blankClause(): BuilderClause {
  return { left: { kind: 'literal', value: '' }, op: '==', right: { kind: 'literal', value: '' } };
}

function blankModel(): BuilderModel {
  return { joiner: 'AND', clauses: [blankClause()] };
}

function computeInitial(value: string): { mode: 'builder' | 'raw'; model: BuilderModel; rawText: string } {
  if (value.trim() === '') return { mode: 'builder', model: blankModel(), rawText: value };
  const parsed = expressionToBuilder(value);
  if (parsed) return { mode: 'builder', model: parsed, rawText: value };
  return { mode: 'raw', model: blankModel(), rawText: value };
}

function isSideComplete(side: BuilderSide): boolean {
  switch (side.kind) {
    case 'literal':
      return side.value !== '' && side.value !== undefined;
    case 'ref':
      return !!side.name;
    case 'aggregate': {
      if (!side.fn || !side.collection) return false;
      if (side.fn !== 'TXN_COUNT' && !side.field) return false;
      if (side.filter) {
        if (!side.filter.field) return false;
        if (side.filter.value === '' || side.filter.value === undefined) return false;
      }
      return true;
    }
  }
}

function isClauseComplete(clause: BuilderClause): boolean {
  return isSideComplete(clause.left) && !!clause.op && isSideComplete(clause.right);
}

function isModelComplete(model: BuilderModel): boolean {
  return model.clauses.length > 0 && model.clauses.every(isClauseComplete);
}

/** Numeric-looking text becomes a number literal (unquoted on serialize);
 *  everything else stays a string literal (quoted on serialize). */
function parseLiteralInput(raw: string): string | number {
  const trimmed = raw.trim();
  if (trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return raw;
}

/** Shared guard for literal-ish value inputs (the literal side's own value,
 *  and an aggregate's filter value): `'` breaks `serializeLiteral`'s naive
 *  quoting, so it's blocked at the input rather than let through to commit.
 *  Blocked input surfaces `setHint(true)` and does not call `commit`. */
function guardedLiteralChange(raw: string, setHint: (blocked: boolean) => void, commit: (value: string | number) => void) {
  if (raw.includes("'")) {
    setHint(true);
    return;
  }
  setHint(false);
  commit(parseLiteralInput(raw));
}

function defaultAggregateSide(pickerData: ExpressionPickerData): BuilderSide {
  const collection: BuilderCollection = 'check';
  return { kind: 'aggregate', fn: 'TXN_SUM', collection, field: pickerData.collections[collection]?.[0] };
}

function defaultRefSide(pickerData: ExpressionPickerData): BuilderSide {
  return { kind: 'ref', name: pickerData.transactionRefs[0] ?? '' };
}

function defaultLiteralSide(): BuilderSide {
  return { kind: 'literal', value: '' };
}

/** Validates the whole serialized expression first — one parse+validate
 *  pass, which is all the common (valid) case costs. Only when that fails
 *  does it fall back to re-validating each clause's own serialization
 *  individually, to attribute the error to a specific builder row.
 *  Only called once every clause is structurally complete. */
function findInvalidClause(model: BuilderModel): { index: number; message: string } | null {
  const whole = builderToExpression(model);
  const wholeErrors = validateTransactionExpression(whole);
  if (wholeErrors.length === 0) return null;

  for (let i = 0; i < model.clauses.length; i++) {
    const single = builderToExpression({ joiner: 'AND', clauses: [model.clauses[i]!] });
    const errors = validateTransactionExpression(single);
    if (errors.length > 0) return { index: i, message: errors[0]! };
  }
  return { index: model.clauses.length - 1, message: wholeErrors[0]! };
}

// ─── Side editor ────────────────────────────────────────────────────────

interface SideEditorProps {
  side: BuilderSide;
  pickerData: ExpressionPickerData;
  onChange: (next: BuilderSide) => void;
}

function SideEditor({ side, pickerData, onChange }: SideEditorProps) {
  const kindId = useId();
  const fnId = useId();
  const collId = useId();
  const fieldId = useId();
  const refId = useId();
  const refCustomId = useId();
  const literalId = useId();
  const filterFieldId = useId();
  const filterOpId = useId();
  const filterValId = useId();
  const codeListId = useId();

  const [literalHint, setLiteralHint] = useState(false);
  const [filterHint, setFilterHint] = useState(false);

  function changeKind(kind: BuilderSide['kind']) {
    setLiteralHint(false);
    setFilterHint(false);
    if (kind === 'aggregate') onChange(defaultAggregateSide(pickerData));
    else if (kind === 'ref') onChange(defaultRefSide(pickerData));
    else onChange(defaultLiteralSide());
  }

  function handleLiteralValueChange(raw: string) {
    guardedLiteralChange(raw, setLiteralHint, (v) => onChange({ kind: 'literal', value: v }));
  }

  function handleFilterValueChange(raw: string) {
    if (side.kind !== 'aggregate' || !side.filter) return;
    const filter = side.filter;
    guardedLiteralChange(raw, setFilterHint, (v) => onChange({ ...side, filter: { ...filter, value: v } }));
  }

  const refIsCustom = side.kind === 'ref' && !pickerData.transactionRefs.includes(side.name);

  return (
    <div className="expr-side-inner">
      <label className="sr-only" htmlFor={kindId}>Side kind</label>
      <select
        id={kindId}
        aria-label="Side kind"
        className="expr-select expr-select--kind"
        value={side.kind}
        onChange={(e) => changeKind(e.target.value as BuilderSide['kind'])}
      >
        <option value="literal">Literal value</option>
        <option value="ref">Reference</option>
        <option value="aggregate">Aggregate</option>
      </select>

      {side.kind === 'aggregate' && (
        <>
          <label className="sr-only" htmlFor={fnId}>Function</label>
          <select
            id={fnId}
            aria-label="Function"
            className="expr-select"
            value={side.fn}
            onChange={(e) => {
              const fn = e.target.value as BuilderAggFn;
              onChange(
                fn === 'TXN_COUNT'
                  ? { kind: 'aggregate', fn, collection: side.collection, filter: side.filter }
                  : {
                      kind: 'aggregate',
                      fn,
                      collection: side.collection,
                      field: side.field ?? pickerData.collections[side.collection]?.[0] ?? '',
                      filter: side.filter,
                    },
              );
            }}
          >
            {AGG_FNS.map((fn) => (
              <option key={fn} value={fn}>{fn}</option>
            ))}
          </select>

          <label className="sr-only" htmlFor={collId}>Collection</label>
          <select
            id={collId}
            aria-label="Collection"
            className="expr-select"
            value={side.collection}
            onChange={(e) => {
              const collection = e.target.value as BuilderCollection;
              const field = pickerData.collections[collection]?.[0] ?? '';
              onChange({
                kind: 'aggregate',
                fn: side.fn,
                collection,
                ...(side.fn === 'TXN_COUNT' ? {} : { field }),
              });
            }}
          >
            {COLLECTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {side.fn !== 'TXN_COUNT' && (
            <>
              <label className="sr-only" htmlFor={fieldId}>Field</label>
              <select
                id={fieldId}
                aria-label="Field"
                className="expr-select"
                value={side.field ?? ''}
                onChange={(e) => onChange({ ...side, field: e.target.value })}
              >
                <option value="" disabled>Select field…</option>
                {(pickerData.collections[side.collection] ?? []).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </>
          )}

          <label className="expr-filter-toggle">
            <input
              type="checkbox"
              aria-label="Use filter"
              checked={!!side.filter}
              onChange={(e) => {
                if (e.target.checked) {
                  const field = pickerData.collections[side.collection]?.[0] ?? '';
                  onChange({ ...side, filter: { field, op: '==', value: '' } });
                } else {
                  const { filter: _filter, ...rest } = side;
                  onChange(rest as BuilderSide);
                }
              }}
            />
            {' '}Filter
          </label>

          {side.filter && (
            <div className="expr-filter-row">
              <label className="sr-only" htmlFor={filterFieldId}>Filter field</label>
              <select
                id={filterFieldId}
                aria-label="Filter field"
                className="expr-select"
                value={side.filter.field}
                onChange={(e) => onChange({ ...side, filter: { ...side.filter!, field: e.target.value } })}
              >
                {(pickerData.collections[side.collection] ?? []).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>

              <label className="sr-only" htmlFor={filterOpId}>Filter operator</label>
              <select
                id={filterOpId}
                aria-label="Filter operator"
                className="expr-select"
                value={side.filter.op}
                onChange={(e) => onChange({ ...side, filter: { ...side.filter!, op: e.target.value as BuilderOp } })}
              >
                {COMPARISON_OPS.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>

              <label className="sr-only" htmlFor={filterValId}>Filter value</label>
              <input
                id={filterValId}
                aria-label="Filter value"
                className="expr-input"
                value={String(side.filter.value ?? '')}
                list={side.collection === 'allocation' ? codeListId : undefined}
                onChange={(e) => handleFilterValueChange(e.target.value)}
                placeholder="value"
              />
              {side.collection === 'allocation' && pickerData.codeSuggestions && (
                <datalist id={codeListId}>
                  {pickerData.codeSuggestions.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              )}
              {filterHint && <p className="expr-hint" role="alert">{APOSTROPHE_HINT}</p>}
            </div>
          )}
        </>
      )}

      {side.kind === 'ref' && (
        <>
          <label className="sr-only" htmlFor={refId}>Reference</label>
          <select
            id={refId}
            aria-label="Reference"
            className="expr-select"
            value={refIsCustom ? CUSTOM_REF : side.name}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ kind: 'ref', name: val === CUSTOM_REF ? '' : val });
            }}
          >
            {pickerData.transactionRefs.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
            <option value={CUSTOM_REF}>Custom…</option>
          </select>
          {refIsCustom && (
            <>
              <label className="sr-only" htmlFor={refCustomId}>Custom reference</label>
              <input
                id={refCustomId}
                aria-label="Custom reference"
                className="expr-input"
                value={side.name}
                placeholder="field.name"
                onChange={(e) => onChange({ kind: 'ref', name: e.target.value })}
              />
            </>
          )}
        </>
      )}

      {side.kind === 'literal' && (
        <>
          <label className="sr-only" htmlFor={literalId}>Value</label>
          <input
            id={literalId}
            aria-label="Value"
            className="expr-input"
            value={String(side.value ?? '')}
            onChange={(e) => handleLiteralValueChange(e.target.value)}
            placeholder="value"
          />
          {literalHint && <p className="expr-hint" role="alert">{APOSTROPHE_HINT}</p>}
        </>
      )}
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────

export function ExpressionEditor({ value, onChange, pickerData, label }: ExpressionEditorProps) {
  // Computed once on mount (a single parse of `value`, not one per field) —
  // each field's useState below just reads a property off this snapshot.
  const [initial] = useState(() => {
    const init = computeInitial(value);
    // Only worth validating eagerly when the initial render is raw mode —
    // builder mode doesn't show rawErrors until toggleToRaw() recomputes it.
    const rawErrors = init.mode === 'raw' && value.trim() ? validateTransactionExpression(value) : [];
    return { ...init, rawErrors };
  });
  const [mode, setMode] = useState<'builder' | 'raw'>(initial.mode);
  const [model, setModel] = useState<BuilderModel>(initial.model);
  const [rawText, setRawText] = useState<string>(initial.rawText);
  const [rawErrors, setRawErrors] = useState<string[]>(initial.rawErrors);
  const [clauseErrors, setClauseErrors] = useState<Record<number, string>>({});

  const ownedValueRef = useRef(value);
  const joinerId = useId();
  const labelId = useId();

  // Re-sync local state when `value` changes from outside this component's
  // own emissions (e.g. the parent loads a different rule/policy).
  //
  // INTEGRATOR CONTRACT: parents MUST echo the emitted string back into
  // `value` unchanged — the bail-out below relies on exact string equality
  // with the last emission. A parent that trims/normalizes/reserializes the
  // value before storing will fail the equality check and clobber
  // in-progress builder state on every keystroke. (Applies to
  // BalancePolicySection, TransactionRulesSection, FormatColumnsEditor.)
  useEffect(() => {
    if (value === ownedValueRef.current) return;
    const next = computeInitial(value);
    setMode(next.mode);
    setModel(next.model);
    setRawText(next.rawText);
    setRawErrors(value.trim() ? validateTransactionExpression(value) : []);
    setClauseErrors({});
    ownedValueRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commitModel(nextModel: BuilderModel) {
    setModel(nextModel);
    if (!isModelComplete(nextModel)) {
      setClauseErrors({});
      return;
    }
    const invalid = findInvalidClause(nextModel);
    if (invalid) {
      setClauseErrors({ [invalid.index]: invalid.message });
      return;
    }
    setClauseErrors({});
    const serialized = builderToExpression(nextModel);
    ownedValueRef.current = serialized;
    onChange(serialized);
  }

  function updateClause(i: number, patch: Partial<BuilderClause>) {
    commitModel({ ...model, clauses: model.clauses.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  }

  function addClause() {
    commitModel({ ...model, clauses: [...model.clauses, blankClause()] });
  }

  function removeClause(i: number) {
    if (model.clauses.length <= 1) return;
    setClauseErrors((prev) => {
      const next = { ...prev };
      delete next[i];
      return next;
    });
    commitModel({ ...model, clauses: model.clauses.filter((_, idx) => idx !== i) });
  }

  function handleRawChange(next: string) {
    setRawText(next);
    setRawErrors(validateTransactionExpression(next));
    ownedValueRef.current = next;
    onChange(next);
  }

  const canConvertToBuilder = useMemo(() => expressionToBuilder(rawText) !== null, [rawText]);

  function toggleToRaw() {
    const serialized = builderToExpression(model);
    setRawText(serialized);
    setRawErrors(validateTransactionExpression(serialized));
    setMode('raw');
  }

  function toggleToBuilder() {
    const parsed = expressionToBuilder(rawText);
    if (!parsed) return;
    setModel(parsed);
    setClauseErrors({});
    setMode('builder');
  }

  const hintText = `Functions: ${AGG_FNS.join(', ')} · transaction refs: ${pickerData.transactionRefs.join(', ')}`;

  return (
    <div className="expression-editor" role={label ? 'group' : undefined} aria-labelledby={label ? labelId : undefined}>
      {label && <div id={labelId} className="expression-editor__label">{label}</div>}

      <div className="expression-editor__toolbar">
        <button
          type="button"
          data-testid="expr-mode-toggle"
          className="expr-mode-toggle"
          onClick={mode === 'builder' ? toggleToRaw : toggleToBuilder}
          disabled={mode === 'raw' && !canConvertToBuilder}
          title={mode === 'raw' && !canConvertToBuilder ? TOO_COMPLEX_TITLE : undefined}
        >
          {mode === 'builder' ? 'Switch to raw text' : 'Switch to builder'}
        </button>

        {mode === 'builder' && (
          <span className="expr-joiner-wrap">
            <label className="sr-only" htmlFor={joinerId}>Joiner</label>
            <select
              id={joinerId}
              aria-label="Joiner"
              className="expr-select"
              value={model.joiner}
              onChange={(e) => commitModel({ ...model, joiner: e.target.value as 'AND' | 'OR' })}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          </span>
        )}
      </div>

      {mode === 'builder' ? (
        <div className="expression-editor__builder">
          {model.clauses.map((clause, i) => (
            <div key={i} className="expr-clause-row" data-testid={`expr-clause-${i}`}>
              {i > 0 && <span className="expr-joiner-badge">{model.joiner}</span>}
              <div className="expr-side" data-testid={`expr-clause-${i}-left`}>
                <SideEditor side={clause.left} pickerData={pickerData} onChange={(next) => updateClause(i, { left: next })} />
              </div>

              <label className="sr-only" htmlFor={`${joinerId}-op-${i}`}>Comparison operator</label>
              <select
                id={`${joinerId}-op-${i}`}
                aria-label="Comparison operator"
                className="expr-select expr-op-select"
                value={clause.op}
                onChange={(e) => updateClause(i, { op: e.target.value as BuilderOp })}
              >
                {COMPARISON_OPS.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>

              <div className="expr-side" data-testid={`expr-clause-${i}-right`}>
                <SideEditor side={clause.right} pickerData={pickerData} onChange={(next) => updateClause(i, { right: next })} />
              </div>

              <button
                type="button"
                className="expr-clause-remove"
                aria-label={`Remove clause ${i + 1}`}
                onClick={() => removeClause(i)}
                disabled={model.clauses.length <= 1}
              >
                ×
              </button>

              {clauseErrors[i] && (
                <div className="expr-clause-error" role="alert">{clauseErrors[i]}</div>
              )}
            </div>
          ))}
          <button type="button" className="expr-add-clause" onClick={addClause}>
            + Add clause
          </button>
        </div>
      ) : (
        <div className="expression-editor__raw">
          <label className="sr-only" htmlFor={`${joinerId}-raw`}>Raw expression</label>
          <textarea
            id={`${joinerId}-raw`}
            aria-label="Raw expression"
            className="expr-raw-textarea"
            value={rawText}
            onChange={(e) => handleRawChange(e.target.value)}
            rows={3}
            spellCheck={false}
          />
          <div className="expr-raw-validation" data-testid="expr-raw-validation">
            {rawErrors.length === 0 ? (
              <span className="expr-raw-valid">✓ valid expression</span>
            ) : (
              <ul>
                {rawErrors.map((err, i) => (
                  <li key={i} className="expr-raw-error">{err}</li>
                ))}
              </ul>
            )}
          </div>
          <p className="expr-raw-hint" data-testid="expr-raw-hint">{hintText}</p>
          <details className="expr-test-details">
            <summary>Test this formula</summary>
            <FormulaTestPanel formula={rawText} />
          </details>
        </div>
      )}
    </div>
  );
}
